import { FormikErrors, FormikTouched } from "formik";
import React, { ChangeEventHandler, FocusEventHandler, useState } from "react";
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Box, Text, ThemeUIStyleObject } from "theme-ui";
interface FormSelectProps {
    disabled?: boolean | undefined;
    id?: string | undefined;
    placeholder?: string | undefined;
    value: any;
    sx?: any;
    Arrow_sx?: ThemeUIStyleObject;
    onChange: ChangeEventHandler | any | undefined,
    Name?: string | undefined;
    styles?: any;
    menuPortalTarget?: HTMLElement | null;
    menuPosition?: 'absolute' | 'fixed';
    menuPlacement?: 'auto' | 'bottom' | 'top';
    DefultSelect?: string;
    defaultMenuIsOpen?: boolean;
    className?: string;
    options?: any;
    touched?: boolean | FormikTouched<any> | FormikTouched<any>[] | undefined,
    errors?: string | string[] | FormikErrors<any> | FormikErrors<any>[] | undefined,
    Max_Length?: number,
    onBlur?: FocusEventHandler | undefined,
    handleSubmit?: () => void,
    autoFocus?: boolean | undefined;
    description?: string | undefined;
    label?: string | undefined;
    asterisk?: boolean;
    mb?: number | undefined | Array<number>;
    mt?: number | undefined | Array<number>;
    isMulti?: boolean;
    isCreatable?: boolean;
    classNames?: any;
    scrollable?: boolean;
    formatOptionLabel?: any;
    onFocus?: any;
    isValidNewOption?: (inputValue: string, selectValue: unknown, selectOptions: readonly { label: string; value: string }[]) => boolean;
    closeMenuOnScroll?: boolean | ((event: Event) => boolean);
}

const FormSelect: React.FC<FormSelectProps> = ({
    Name = "",
    onChange = () => null,
    onFocus,
    isValidNewOption,
    value,
    sx,
    Arrow_sx,
    id = "",
    placeholder = "",
    DefultSelect = "",
    defaultMenuIsOpen = false,
    styles,
    errors = "",
    disabled = false,
    options = [],
    onBlur = () => null,
    handleSubmit = () => null,
    autoFocus = false,
    description = "",
    touched = false,
    label,
    className,
    classNames,
    asterisk,
    mb,
    mt,
    isMulti = false,
    isCreatable = false,
    menuPortalTarget,
    menuPosition = 'fixed',
    menuPlacement,
    scrollable = false,
    formatOptionLabel,
    closeMenuOnScroll = false,
}) => {



    const [filteredOptions, setFilteredOptions] = useState<{ value: string, label: string }[]>([]);

    const SelectComponent = isCreatable ? CreatableSelect : Select;

    const customFilter = (option: { value: string, label: string }, inputValue: string) => {
        if (!inputValue) {
            return true;
        }

        const lowerCasedInput = inputValue.toLowerCase();
        const label = option.label.toLowerCase();

        return label.includes(lowerCasedInput);
    };

    const handleInputChange = (inputValue: string, { action }: any) => {
        if (!scrollable || action === 'input-change') {
            // Filter options based on input value
            const filtered = options.filter((option: { value: string, label: string }) => customFilter(option, inputValue)).slice(0, 10);
            setFilteredOptions(filtered);
        }
    };

    const rawVal = typeof value === 'object' && value !== null && !Array.isArray(value) && 'value' in value
        ? (value as { value: unknown }).value
        : value;

    const selectedOption = value
        ? (isMulti
            ? options.filter((opt: { value: unknown }) => (Array.isArray(value) ? value : [value])?.some((v: unknown) => String(typeof v === 'object' && v !== null && 'value' in v ? (v as { value: unknown }).value : v) === String(opt.value)))
            : (options.find((opt: { value: unknown }) => String(opt.value) === String(rawVal)) ||
                (typeof value === 'object' && value !== null && !Array.isArray(value) && 'label' in value
                    ? value
                    : (/^[0-9a-fA-F]{24}$/.test(String(rawVal)) ? null : { label: String(rawVal ?? ''), value: rawVal }))))
        : null;

    const tooltipTitle = isMulti
        ? (Array.isArray(selectedOption) ? selectedOption.map((opt: { label: string }) => opt.label).join(", ") : "")
        : (selectedOption ? (selectedOption as { label: string }).label : "");

    return (
        <Box>
            <Text variant="SelectLabel" sx={sx}>
                {label}
                {asterisk && <span className="mx-1 text-danger">*</span>}
            </Text>
            <Box mb={mb} mt={mt} title={tooltipTitle}>
                {/* merge small default styles so portal menus sit above modals and are positioned correctly */}
                <SelectComponent
                    key={options.value}
                    isDisabled={disabled}
                    isSearchable={true}
                    isMulti={isMulti}
                    options={scrollable ? options : (filteredOptions.length > 0 ? filteredOptions : options.slice(0, 10))}
                    onChange={onChange}
                    onFocus={onFocus}
                    isValidNewOption={isValidNewOption}
                    defaultMenuIsOpen={defaultMenuIsOpen}
                    classNamePrefix="an-simple-select"
                    className={className}
                    classNames={classNames}
                    name={Name}
                    autoFocus={autoFocus}
                    placeholder={placeholder}
                    onInputChange={handleInputChange}
                    formatOptionLabel={formatOptionLabel}
                    id={id}
                    styles={{
                        // defaults
                        menu: (base: any) => ({ ...base, zIndex: 99999 }),
                        menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
                        menuList: (base: any) => ({
                            ...base,
                            ...(scrollable ? { maxHeight: '300px', overflowY: 'auto' } : {}),
                        }),
                        ...(styles || {}),
                    }}
                    menuPortalTarget={menuPortalTarget ?? (typeof document !== 'undefined' ? document.body : null)}
                    menuPosition={menuPosition}
                    menuPlacement={menuPlacement}
                    closeMenuOnScroll={closeMenuOnScroll}
                    theme={(theme) => ({
                        ...theme,
                        colors: {
                            ...theme.colors,
                            primary25: '#f7efe5',
                        },
                    })}
                    onBlur={onBlur}
                    value={selectedOption}
                />
                {errors && touched ? (
                    <Text variant="Inter15Regular30lh" className="text-danger" sx={{ fontSize: 17, lineHeight: "10px" }}>
                        {errors.toString()}
                    </Text>
                ) : (
                    <Text variant="Inter15Regular30lh" sx={{ my: "5px", minHeight: "11px" }}>
                        {description}
                    </Text>
                )}
            </Box>
        </Box >
    );
};

export default FormSelect;
